<script setup lang="ts">
import BuilderBranding from "@/components/common/BuilderBranding.vue";
import { useAppStore } from "@/store/app";
import {
  getCardStyle,
  stripProtocol,
  type ScheduleAppearance,
  type VisibilityState,
} from "@/util/formSchedule";
import { ArrowUpRightIcon, EyeIcon } from "@gohighlevel/ghl-icons/24/outline";
import DOMPurify from "dompurify";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n();
const store = useAppStore();

const props = defineProps<{
  state: VisibilityState;
  appearance: ScheduleAppearance;
}>();

const isRedirect = computed(() => props.state.mode === "redirect");

// Admin-authored rich text, but still sanitized before rendering — the HTML
// round-trips through persistence, so it must not be treated as trusted.
const sanitizedHtml = computed(() => DOMPurify.sanitize(props.state.html));

const cardStyle = computed(() => getCardStyle(props.appearance));

const pageStyle = computed(() => ({
  background: props.appearance.pageBackground,
}));

const urlDisplay = computed(
  () => stripProtocol(props.state.redirectUrl) || "example.com"
);
</script>

<template>
  <div class="preview">
    <p class="preview__label">
      <EyeIcon class="preview__label-icon" />
      {{ t("form.scheduleVisibility.preview.label") }}
      <span class="preview__label-hint">{{
        t("form.scheduleVisibility.preview.labelHint")
      }}</span>
    </p>
    <div class="preview__page" :style="pageStyle">
      <!-- Redirect: show where visitors will be sent -->
      <div v-if="isRedirect" class="preview__redirect-card">
        <span class="preview__redirect-icon">
          <ArrowUpRightIcon class="h-6 w-6" />
        </span>
        <p class="preview__redirect-label">
          {{ t("form.scheduleVisibility.preview.redirectTo") }}
        </p>
        <a class="preview__redirect-url">{{ urlDisplay }}</a>
      </div>

      <!-- Page: render the authored rich-text card styled by Appearance -->
      <div v-else class="preview__card" :style="cardStyle">
        <div class="preview__content" v-html="sanitizedHtml"></div>
        <!-- Same bottom-of-card placement as the form/survey/quiz builder previews. -->
        <BuilderBranding
          :is-branding-active="appearance.agencyBranding"
          :company="store.company"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.preview {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.preview__label {
  display: flex;
  align-items: center;
  gap: 7px;
  font-size: 13px;
  font-weight: 600;
  color: #475467;
}

.preview__label-icon {
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  color: #667085;
}

.preview__label-hint {
  font-weight: 400;
  color: #98a2b3;
}

/* Viewport-like preview: height tracks the browser window (never the card),
   so taller cards scroll inside it. Flex page with no padding so the card is
   placed purely by its own appearance margins — flex auto margins absorb free
   space on BOTH axes (all-auto = dead center), 0 = flush with the page edge.
   Centering via the card's auto margins (not justify-content) stays
   scroll-safe when the card is taller than the page. align-items guards
   against cross-axis stretch; auto margins override it when present. */
.preview__page {
  display: flex;
  align-items: flex-start;
  height: clamp(260px, 38vh, 480px);
  overflow-y: auto;
  border-radius: 12px;
}

/* Flex column so the branding stays pinned to the card bottom when a
   taller cardHeight leaves extra vertical space. */
.preview__card {
  display: flex;
  flex-direction: column;
}

.preview__content {
  flex: 1 1 auto;
}

.preview__content :deep(p) {
  margin: 0;
}

/* The redirect mock isn't styled by Appearance — center it in the flex page
   and keep its own side breathing room since the page has no padding. */
.preview__redirect-card {
  width: calc(100% - 48px);
  max-width: 440px;
  margin: auto;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 14px;
  box-shadow: 0 12px 16px -4px rgba(16, 24, 40, 0.08),
    0 4px 6px -2px rgba(16, 24, 40, 0.03);
  padding: 40px 32px;
  text-align: center;
}

.preview__redirect-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 999px;
  background: #eff4ff;
  color: #155eef;
  margin-bottom: 18px;
}

.preview__redirect-label {
  font-size: 15px;
  color: #475467;
  margin-bottom: 8px;
}

.preview__redirect-url {
  font-size: 16px;
  font-weight: 600;
  color: #155eef;
  word-break: break-all;
}
</style>
